import { c as _c } from "react/compiler-runtime";
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import type { SubsectionType as Subsection } from '@/components/entity-onboarding-details';
import { useEntityContext } from '@/entities/components/details/hooks/use-entity-context';
type Section = {
  key: Subsection;
  title: string;
  isShown: boolean;
};

/** The previous layout's sections, in its order. Duplicates and insights live below the onboardings there. */
const useSections = onboarding => {
  const $ = _c(71);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "onboardings"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const {
    kind
  } = useEntityContext();
  const isPerson = kind === "person";
  const isBusiness = kind === "business";
  const isCompleteKyb = onboarding.kind === "kyb" && (onboarding.status === "pass" || onboarding.status === "fail");
  const hasBusinessInsights = isBusiness && isCompleteKyb && Boolean(onboarding.seqno);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = onboarding.kind !== "document";
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      key: "rules",
      title: t1,
      isShown: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t(_temp2);
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = {
      key: "risk-signals",
      title: t4,
      isShown: true
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t(_temp3);
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isPerson || $[13] !== t6) {
    t7 = {
      key: "user-data",
      title: t6,
      isShown: isPerson
    };
    $[12] = isPerson;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp4);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isPerson || $[18] !== t8) {
    t9 = {
      key: "field-validations",
      title: t8,
      isShown: isPerson
    };
    $[17] = isPerson;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t(_temp5);
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== isBusiness || $[23] !== t10) {
    t11 = {
      key: "business-data",
      title: t10,
      isShown: isBusiness
    };
    $[22] = isBusiness;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t(_temp6);
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== hasBusinessInsights || $[28] !== t12) {
    t13 = {
      key: "business-details",
      title: t12,
      isShown: hasBusinessInsights
    };
    $[27] = hasBusinessInsights;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t(_temp7);
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== hasBusinessInsights || $[33] !== t14) {
    t15 = {
      key: "people",
      title: t14,
      isShown: hasBusinessInsights
    };
    $[32] = hasBusinessInsights;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t(_temp8);
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== hasBusinessInsights || $[38] !== t16) {
    t17 = {
      key: "registrations",
      title: t16,
      isShown: hasBusinessInsights
    };
    $[37] = hasBusinessInsights;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = t(_temp9);
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== hasBusinessInsights || $[43] !== t18) {
    t19 = {
      key: "watchlist",
      title: t18,
      isShown: hasBusinessInsights
    };
    $[42] = hasBusinessInsights;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t(_temp10);
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== hasBusinessInsights || $[48] !== t20) {
    t21 = {
      key: "offices",
      title: t20,
      isShown: hasBusinessInsights
    };
    $[47] = hasBusinessInsights;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t(_temp11);
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = {
      key: "documents",
      title: t22,
      isShown: true
    };
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t) {
    t24 = t(_temp12);
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = {
      key: "more-information",
      title: t24,
      isShown: true
    };
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t11 || $[59] !== t13 || $[60] !== t15 || $[61] !== t17 || $[62] !== t19 || $[63] !== t21 || $[64] !== t23 || $[65] !== t25 || $[66] !== t3 || $[67] !== t5 || $[68] !== t7 || $[69] !== t9) {
    const sections = [t3, t5, t7, t9, t11, t13, t15, t17, t19, t21, t23, t25];
    t26 = sections.filter(_temp13);
    $[58] = t11;
    $[59] = t13;
    $[60] = t15;
    $[61] = t17;
    $[62] = t19;
    $[63] = t21;
    $[64] = t23;
    $[65] = t25;
    $[66] = t3;
    $[67] = t5;
    $[68] = t7;
    $[69] = t9;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  return t26;
};
export default useSections;
function _temp($) {
  return $.rules.title;
}
function _temp2($_0) {
  return $_0.riskSignals.title;
}
function _temp3($_1) {
  return $_1.userData.title;
}
function _temp4($_2) {
  return $_2.fieldValidations.title;
}
function _temp5($_3) {
  return $_3.businessData.title;
}
function _temp6($_4) {
  return $_4.businessDetails.title;
}
function _temp7($_5) {
  return $_5.people.title;
}
function _temp8($_6) {
  return $_6.registrations.title;
}
function _temp9($_7) {
  return $_7.watchlist.title;
}
function _temp10($_8) {
  return $_8.offices.title;
}
function _temp11($_9) {
  return $_9.documents.title;
}
function _temp12($_10) {
  return $_10.moreInformation.title;
}
function _temp13(section) {
  return section.isShown;
}